import { forwardRef } from 'react';

import { ClickableDiv } from '@wwe-portal/ui/components/button';
import { LevelStar } from '@wwe-portal/ui/features/roster/roster-star/level-star';
import type { RosterLevelType } from '@wwe-portal/entities/roster.type';
import { cn } from '@wwe-portal/ui/lib/lib';

import BronzeIcon from './bronze-icon.webp';
import GoldIcon from './gold-icon.webp';
import SilverIcon from './silver-icon.webp';

const RosterIcon = {
  B: { label: 'Bronze', icon: BronzeIcon },
  S: { label: 'Silver', icon: SilverIcon },
  G: { label: 'Gold', icon: GoldIcon },
} as const;

export interface RosterStarColorProps {
  onClick?: (color: RosterLevelType) => void;
  tier: RosterLevelType;
  variant: 'default' | 'active' | 'selected';
}

export const RosterStarColor = forwardRef<HTMLDivElement, RosterStarColorProps>(
  ({ onClick, tier, variant = 'default' }: RosterStarColorProps, ref) => {
    const glowing = variant === 'active' || variant === 'selected';
    return (
      <ClickableDiv
        className="text-text400 flex cursor-pointer gap-2 text-sm"
        onClick={() => onClick?.(tier)}
        ref={ref}
      >
        <div className={cn('relative size-[32px]')}>
          <div
            className={cn(
              'absolute -inset-[1px] box-border rounded-full border-[0.4px] border-solid border-[#35488D]',
              {
                'border-[2px] blur-[1px] drop-shadow-[0_4px_4px_rgba(0,0,0,0.1)]':
                  glowing,
                'border-[#FEFEFE] shadow-[#FEFEFE]': glowing && tier === 'S',
                'border-[#FFC122] shadow-[#FFC122]': glowing && tier === 'B',
                'border-[#FFF500] shadow-[#FFF500]': glowing && tier === 'G',
                'animate-shadow-pulse': variant === 'selected',
              }
            )}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex size-[30px] items-center justify-center rounded-full bg-gradient-to-r from-[#1B7BEB] to-[#184D8B]">
              <LevelStar
                className={cn('drop-shadow-[2px_2px_2px_rgba(0,0,0,0.35)]', {
                  'animate-drop-shadow-pulse': variant === 'selected',
                })}
                level={tier}
                size={20}
              />
            </div>
          </div>
        </div>
        <div className="xs:flex hidden flex-col justify-center text-center">
          <span>{RosterIcon[tier].label}</span>
        </div>
      </ClickableDiv>
    );
  }
);

RosterStarColor.displayName = 'RosterStarColor';
